import {
	AppCategory,
	AppColorViewModel,
	AppType,
	CalendarDestinationApp,
	ClientlessRdpDestinationWindow,
	DestinationWindow,
	EmailDestinationApp,
	SaveAppColorModel
} from "../Apps/common";
import { AppImageDisplayInfo, AppImageIdentifier } from "../AppIcon/common";
import {
	Browser,
	DatabaseOrTempDtoId,
	Device,
	IpAddressRange,
	OperatingSystem,
	PermissionSetModel,
	PermissionsUnionModel,
	TempIdToDatabaseIdMappingModel,
} from "../common";
import { UsersAndUserGroupsDataModel } from "../UserGroupsManagement/userGroupsManagement";
import {
	GridPosition,
	GridDimension,
	TileFactoryConfiguration
} from "./common";
import { environmentHttpClient as httpClient } from "../httpClient";
import { ObjectWithClientId } from "client/Misc/clientId";
import { DatabaseOrTempObjectId } from "client_legacy/ndaw/tempOrDatabaseId";
import { AppImageType } from "../AppIcon/appIcon";
import { AfasResourceKind } from "./LiveInformation/Afas/afasLiveInformation";
import { HubTileLayoutKind } from "../Hub/common";
import { HubItemKind } from "../Hub/hubItemCategories";
import { Guid } from "client_legacy/ndaw/guid";
import { TokenInfo } from "../Apps/PowerBi/powerBi";
import { FolderIdentifier as DocumentsFolderIdentifier } from "../Workspace/LiveInformation/Documents/documentsLiveInformation";
import {
	ContentKind,
	FolderIdentifier as EmailFolderIdentifier,
	FolderIdentifierModel,
	MailboxIdentifier,
	SpecificMailboxIdentifier,
	SpecificMailboxIdentifierModel,
	WellKnownFolderIdentifier,
	WellKnownFolderIdentifierModel
} from "../Workspace/LiveInformation/Email/emailLiveInformation";
import { MagisterResourceKind } from "./LiveInformation/Magister/magisterLiveInformation";
import { NedapOnsResourceKind } from "../Workspace/LiveInformation/NedapOns/nedapOnsLiveInformation";
import { RssFeedType } from "../Workspace/LiveInformation/Rss/rssLiveInformation";
import { ZaaksysteemResourceKind } from "../Workspace/LiveInformation/Zaaksysteem/zaaksysteemLiveInformation";
import { ZenyaDocResourceKind } from "./LiveInformation/ZenyaDoc/zenyaDocLiveInformation";
import { LiveTileHeaderStyle, ThemeDictionary } from "../Branding/branding";
import {
	AutotaskConnectionIdentifierViewModel,
	ConfigurableAddressBookAppContactSource,
	NedapOnsConnectionIdentifierViewModel,
	RemoteDesktopProtocolClientKind,
	SharePointAction,
	TeamsAction,
	TopDeskConnectionIdentifierViewModel,
	VilansKickConnectionIdentifierViewModel
} from "../Apps/EditApp/saveApp";
import { TagViewModel } from "../Tags/tags";
import { SpaceUserInfo } from "../TitleBar/GlobalNavigation/globalNavigation";
import { isNotNil } from "client/Misc/utils";
import { IntegrationFlowKind } from "../IntegrationBuilder/integrationBuilderFlows";

export interface ClientWorkspaceModel {
	tileGroups: readonly ClientTileGroupModel[];
	spaces: readonly ClientSpaceModel[];
	maintenanceWindows: readonly MaintenanceWindowModel[] | null;
	conditionalAccessDisabledApps: readonly ConditionalAccessDisabledApps[] | null;
	conditionalAccessDisabledTileGroups: readonly ConditionalAccessDisabledTileGroup[] | null;
}

export type UserWorkspacePermissionsModel = {
	canEditOwnWorkspace: boolean;
	canCreateSharedGroups: boolean;
	canChangeDefaultSharedGroupsOrder: boolean;
	canEmbedTileGroups: boolean;
	canManageSharedSpaces: boolean;
};

export type WorkspaceBrandingSettings = {
	liveTileHeaderStyle: LiveTileHeaderStyle;
	backgroundUrl: string | null;
	logoUrls: ThemeDictionary<string>;
};

export type WorkspaceSettings = {
	userPermissions: UserWorkspacePermissionsModel;
	automaticallyReorderTilesForNoneEditableTileGroups: boolean;
	branding: WorkspaceBrandingSettings;
	isPoweredByTextEnabled: boolean;
};

export type WorkspaceViewModel = {
	settings: WorkspaceSettings;
	tileFactoryConfiguration: TileFactoryConfiguration;
	model: ClientWorkspaceModel;
};

export type ClientTileGroupSpacePreferenceModel = {
	tileGroupId: number;
	isHidden: boolean;
};

export enum SpaceAccessKind {
	Access = 1,
	Edit = 2
}

export type ClientSpaceModel = {
	id: number;
	name: string;
	kind: SpaceKind;
	tileGroupsPreferences: ClientTileGroupSpacePreferenceModel[];

	/// <summary>
	/// NOTE: does not take into account Kiosk mode
	/// </summary>
	accessKind: SpaceAccessKind;
};

export enum SpaceKind {
	Personal = 1,
	Shared = 2
}

export enum TileGroupAccessKind
{
	NoAccess = 0,
	Access = 1,
	Edit = 2
}

export type ClientTileGroupModel = {
	id: number;
	name: string;
	type: TileGroupType;
	width: TileGroupWidth;
	tiles: ClientTileModel[];
	accessKind: TileGroupAccessKind;
	isHiddenByDefaultOnPersonalSpace: boolean;
};

export enum TileGroupType {
	Personal = 1,
	Shared = 2
}

export enum TileGroupWidth {
	Regular = 1,
	Wide = 2
}

export type ClientTileModelBase = {
	id: number;
	environmentAppId: number;
	name: string;
	color: AppColorViewModel;
	imageDisplayInfo: AppImageDisplayInfo;
	isConfiguredCorrectly: boolean;
	position: GridPosition;
	dimension: GridDimension;
	appIsNotAccessible: boolean;
	appIsLicensed: boolean;
	shouldHideName: OptionalModel<boolean>;
};

export type AddressBookClientTileModel = ClientTileModelBase & {
	type: AppType.AddressBook;
	contactSources: readonly ConfigurableAddressBookAppContactSource[];
	defaultView: AddressBookViewKind;
};

export type AfasTabDisplayModel = {
	uniqueId: Guid;
	name: string;
	resourceKind: AfasResourceKind;
};

export type AfasClientTileModel = ClientTileModelBase & {
	type: AppType.Afas;
	destinationWindow: DestinationWindow;
	connectionId: number;
	tabs: AfasTabDisplayModel[];
};

export type AutotaskClientTileModel = ClientTileModelBase & {
	type: AppType.Autotask;
	destinationWindow: DestinationWindow;
	connection: AutotaskConnectionIdentifierViewModel;
};

export type AzureAppClientTileModel = ClientTileModelBase & {
	type: AppType.AzureApp;
	destinationWindow: DestinationWindow;
};

export type CalendarClientTileModel = ClientTileModelBase & {
	type: AppType.Office365Calendar;
	destinationApp: CalendarDestinationApp;
	destinationWindow: DestinationWindow;
};

export type ClientlessRdpClientTileModel = ClientTileModelBase & {
	type: AppType.ClientlessRdp;
	destinationWindow: ClientlessRdpDestinationWindow;
};

export type ConnectWiseClientTileModel = ClientTileModelBase & {
	type: AppType.ConnectWise;
	destinationWindow: DestinationWindow;
};

export type DocumentsTabDisplayModel = {
	uniqueId: Guid;
	name: string;
	folder: DocumentsFolderIdentifier;
};

export type DocumentsClientTileModel = ClientTileModelBase & {
	type: AppType.Documents;
	destinationWindow: DestinationWindow;
	tabs: DocumentsTabDisplayModel[];
};

export type EmailAppTab = {
	uniqueId: Guid;
	name: string;
	contentKind: ContentKind;
	mailbox: MailboxIdentifier;
	folder: EmailFolderIdentifier;
};

export type EmailClientTileModel = ClientTileModelBase & {
	type: AppType.Email;
	destinationApp: EmailDestinationApp;
	destinationWindow: DestinationWindow;
	tabs: EmailAppTab[];
};

export type EmbedAppClientTileModel = ClientTileModelBase & {
	type: AppType.Embed;
	navigationUrl: string;
	hideHeader: string;
	hideFooter: string;
};

export type IntegrationClientTileModel = ClientTileModelBase & {
	type: AppType.Integration;
	connectionId: number;
	destinationWindow: DestinationWindow;
	navigationUrl: string | null;
	hideHeader: boolean;
	hideFooter: boolean;
	flowSettings: IntegrationTileFlowSettingsModel;
};

export type GeneralIntegrationTileFlowSettingsModel = {
	kind: IntegrationFlowKind.General;
};

export type PlanningIntegrationTileFlowSettingsModel = {
	kind: IntegrationFlowKind.Planning;
	layout: IntegrationTileLayoutModel;
};

export type IntegrationTileFlowSettingsModel =
	| GeneralIntegrationTileFlowSettingsModel
	| PlanningIntegrationTileFlowSettingsModel;

export type CalendarIntegrationTileLayoutModel = {
	kind: IntegrationTileLayoutKind.Calendar;
	defaultView: IntegrationTileCalendarViewKind;
	showHoursColumn: boolean;
};

export type ListIntegrationTileLayoutModel = {
	kind: IntegrationTileLayoutKind.List;
};

export type IntegrationTileLayoutModel =
	| CalendarIntegrationTileLayoutModel
	| ListIntegrationTileLayoutModel;

export enum IntegrationTileLayoutKind {
	Calendar = 1,
	List = 2
}

export enum IntegrationTileCalendarViewKind {
	Day = 1,
	Week = 2
}

export type GoogleClientTileModel = ClientTileModelBase & {
	type: AppType.Google;
	destinationWindow: DestinationWindow;
};

export type HubItemClientTileModel = ClientTileModelBase & {
	type: AppType.Hub;
	layout: DisplayHubTileLayout;
	hideHeader: boolean;
	hideFooter: boolean;
};

export type LiquitClientTileModel = ClientTileModelBase & {
	type: AppType.Liquit;
	destinationWindow: DestinationWindow;
	connectionId: number;
};

export type MagisterTabDisplayModel = {
	uniqueId: Guid;
	name: string;
	resourceKind: MagisterResourceKind;
};

export type MagisterClientTileModel = ClientTileModelBase & {
	type: AppType.Magister;
	destinationWindow: DestinationWindow;
	connectionId: number;
	tabs: readonly MagisterTabDisplayModel[];
};

export type NedapOnsTabDisplayModel = {
	uniqueId: Guid;
	name: string;
	resourceKind: NedapOnsResourceKind;
};

export type NedapOnsClientTileModel = ClientTileModelBase & {
	type: AppType.NedapOns;
	destinationWindow: DestinationWindow;
	connection: NedapOnsConnectionIdentifierViewModel;
	tabs: readonly NedapOnsTabDisplayModel[];
};

export type PowerBiClientTileModel = ClientTileModelBase & {
	type: AppType.PowerBi;
	powerBiTileId: string;
	dashboardId: string;
	tokenInfo: TokenInfo | null;
	embedUrlBase: string;
};

export type RssTileTabModel = {
	uniqueId: Guid;
	name: string;
};

export type RssClientTileModel = ClientTileModelBase & {
	type: AppType.Rss;
	tabs: RssTileTabModel[];
};

export type SharePointClientTileModel = ClientTileModelBase & {
	type: AppType.SharePoint;
	enabledActions: SharePointAction;
	destinationWindow: DestinationWindow;
};

export type ShortcutClientTileModel = ClientTileModelBase & {
	type: AppType.Url;
	destinationWindow: DestinationWindow;
};

export type TeamsClientTileModel = ClientTileModelBase & {
	type: AppType.Teams;
	enabledActions: TeamsAction;
	destinationWindow: DestinationWindow;
};

export type ToDoClientTileModel = ClientTileModelBase & {
	type: AppType.ToDo;
	destinationWindow: DestinationWindow;
};

export type TopDeskClientTileModel = ClientTileModelBase & {
	type: AppType.TopDesk;
	destinationWindow: DestinationWindow;
	connection: TopDeskConnectionIdentifierViewModel;
};

export type VilansKickClientTileModel = ClientTileModelBase & {
	type: AppType.VilansKick;
	destinationWindow: DestinationWindow;
	connection: VilansKickConnectionIdentifierViewModel;
};

export type VmwareHorizonAppClientTileModel = ClientTileModelBase & {
	type: AppType.VmwareHorizonApp;
	destinationWindow: DestinationWindow;
};

export enum YammerFeedKind {
	AllCompany = 0,
	OtherGroup = 1,
	Home = 2
}

export type YammerTabDisplayModel = {
	uniqueId: Guid;
	name: string;
	feedKind: YammerFeedKind;
	feedId?: number;
};

export type YammerClientTileModel = ClientTileModelBase & {
	type: AppType.Yammer;
	tabs: YammerTabDisplayModel[];
};

export type YouTubeClientTileModel = ClientTileModelBase & {
	type: AppType.YouTube;
	videoId?: string | nil;
	playlistId?: string | nil;
	hideHeader: string;
	hideFooter: string;
};

export type ZaaksysteemTabDisplayModel = {
	uniqueId: Guid;
	name: string;
	resourceKind: ZaaksysteemResourceKind;
};

export type ZaaksysteemClientTileModel = ClientTileModelBase & {
	type: AppType.Zaaksysteem;
	destinationWindow: DestinationWindow;
	connectionId: number;
	tabs: ZaaksysteemTabDisplayModel[];
};

export type ZenyaDocTabDisplayModel = {
	uniqueId: Guid;
	name: string;
	resourceKind: ZenyaDocResourceKind;
};

export type ZenyaDocClientTileModel = ClientTileModelBase & {
	type: AppType.ZenyaDoc;
	destinationWindow: DestinationWindow;
	connectionId: number;
	tabs: readonly ZenyaDocTabDisplayModel[];
};

export type ZermeloClientTileModel = ClientTileModelBase & {
	type: AppType.Zermelo;
	destinationWindow: DestinationWindow;
	connectionId: number;
};

export type WindowsVirtualDesktopClientTileModel = ClientTileModelBase & {
	type: AppType.WindowsVirtualDesktop;
	clientKind: RemoteDesktopProtocolClientKind;
};

// This type doesn't exist on the backend (it is just ClientTileModel), however for typescript to be
// helpful, we need it. I.e. in the frontend we don't use inheritance for ClientTileModel but a union
export type GenericClientTileModel = ClientTileModelBase & {
	type:
		| AppType.Birthday
		| AppType.CitrixXenApp
		| AppType.HowTo
		| AppType.LocalApp
		| AppType.Office365Excel
		| AppType.Office365LyncMeeting
		| AppType.Office365OneNote
		| AppType.Office365PeopleScreen
		| AppType.Office365PowerPoint
		| AppType.Office365Word
		| AppType.Unknown;
};

export type ClientTileModel =
	| AddressBookClientTileModel
	| AfasClientTileModel
	| AutotaskClientTileModel
	| AzureAppClientTileModel
	| CalendarClientTileModel
	| ClientlessRdpClientTileModel
	| ConnectWiseClientTileModel
	| DocumentsClientTileModel
	| EmailClientTileModel
	| EmbedAppClientTileModel
	| IntegrationClientTileModel
	| GoogleClientTileModel
	| HubItemClientTileModel
	| LiquitClientTileModel
	| MagisterClientTileModel
	| NedapOnsClientTileModel
	| PowerBiClientTileModel
	| RssClientTileModel
	| SharePointClientTileModel
	| ShortcutClientTileModel
	| TeamsClientTileModel
	| ToDoClientTileModel
	| TopDeskClientTileModel
	| VilansKickClientTileModel
	| VmwareHorizonAppClientTileModel
	| YammerClientTileModel
	| YouTubeClientTileModel
	| ZaaksysteemClientTileModel
	| ZenyaDocClientTileModel
	| ZermeloClientTileModel
	| WindowsVirtualDesktopClientTileModel
	| GenericClientTileModel;

export type MaintenanceWindowModel = {
	endsOn: string;
	notificationMessage: string;
	tileIds: number[];
};

export type ConditionalAccessDisabledApps = {
	reason: string;
	hideTiles: boolean;
	tileIds: number[];
};

export type ConditionalAccessDisabledTileGroup = {
	reason: string;
	tileGroupId: number;
};

export type SaveWorkspaceModel = {
	tileGroups: SaveTileGroupModel[];
	space: OptionalModel<SaveSpaceModel>;
};

export type SaveSpaceModel = {
	id: number;
	kind: SpaceKind;
	tileGroupsPreferences: SaveTileGroupSpacePreferenceModel[];
};

export type SaveTileGroupSpacePreferenceModel = {
	tileGroupId: DatabaseOrTempDtoId;
	isHidden: boolean;
};

export type SaveTileGroupModel = {
	id: DatabaseOrTempDtoId;
	name: string;
	tiles: SaveTileModel[];
	type: TileGroupType;
	width: TileGroupWidth;
	isHiddenByDefaultOnPersonalSpace: boolean;
};

export type SaveTileModel = {
	id: number;
	position: GridPosition;
	dimension: GridDimension;
};

export type SaveWorkspaceResponse = {
	addedItemsIdsMap: TempIdToDatabaseIdMappingModel[];
};

export type SaveWorkspaceFailedResponse = {
	reasons: string[];
	missingTileIds: number[];
};

export type EditSharedGroupModel = {
	id: number;
	name: string;
	isHiddenByDefaultOnPersonalSpace: boolean;
	everyoneHasAccess: boolean;
	whoHasAccess: UserAndUserGroupIdsModel;
	owners: UserAndUserGroupIdsModel;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
	conditionalAccessSettings: TileGroupConditionalAccessSettingsModel;
};

export type TileGroupConditionalAccessSettingsModel = {
	restrictDevices: boolean;
	devices: Device[];
	restrictOperatingSystems: boolean;
	operatingSystems: OperatingSystem[];
	restrictBrowsers: boolean;
	browsers: Browser[];
	restrictIp: boolean;
	whitelistIpRanges: IpAddressRange[];
	blacklistIpRanges: IpAddressRange[];
};

export type SaveTileGroupConditionalAccessSettingsModel = {
	restrictDevices: boolean;
	devices: Device[];
	restrictOperatingSystems: boolean;
	operatingSystems: OperatingSystem[];
	restrictBrowsers: boolean;
	browsers: Browser[];
	restrictIp: boolean;
	whitelistIpRanges: ObjectWithClientId<IpAddressRange>[];
	blacklistIpRanges: ObjectWithClientId<IpAddressRange>[];
};

export type SaveSharedTileGroupSettingsModel = {
	tileGroupId: number;
	everyoneHasAccess: boolean;
	whoHasAccess: UserAndUserGroupIdsModel | null;
	owners: UserAndUserGroupIdsModel;
	isHiddenByDefaultOnPersonalSpace: boolean;
	conditionalAccessSettings: SaveTileGroupConditionalAccessSettingsModel;
};

export type UserAndUserGroupIdsModel = {
	userIds: number[];
	userGroupIds: number[];
};

export function loadWorkspace(signal?: AbortSignal): Promise<ClientWorkspaceModel> {
	return httpClient.get<ClientWorkspaceModel>(
		"/Workspace/Load",
		undefined,
		{ signal }
	);
}

export function getWorkspacePageViewModel(signal?: AbortSignal): Promise<WorkspaceViewModel> {
	return httpClient.get<WorkspaceViewModel>(
		"/Workspace/Page",
		undefined,
		{ signal }
	);
}

export function saveWorkspace(
	saveModel: SaveWorkspaceModel,
	signal?: AbortSignal
): Promise<SaveWorkspaceResponse> {
	return httpClient.post<SaveWorkspaceResponse>(
		"/Workspace/Save",
		saveModel,
		{ signal }
	);
}

export function canUserAccessTile(id: number, signal?: AbortSignal): Promise<boolean> {
	return httpClient.get<boolean>(
		`Workspace/CanUserAccessTile/${id}`,
		undefined,
		{ signal }
	);
}

export type PersonalSpaceViewModel = {
	spaceKind: SpaceKind.Personal;
	whoHasAccess: PermissionsUnionModel;
};

export type SharedSpaceViewModel = {
	spaceKind: SpaceKind.Shared;
	name: string;
	whoHasAccess: PermissionsUnionModel;
	owners: PermissionSetModel;
};

export type CreateSpaceViewModel = {
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
};

export function getCreateSpaceViewModel(signal?: AbortSignal): Promise<CreateSpaceViewModel> {
	return httpClient.get<CreateSpaceViewModel>(
		`/Workspace/Spaces/New`,
		undefined,
		{ signal }
	);
}

export type SpaceViewModel =
	| PersonalSpaceViewModel
	| SharedSpaceViewModel;

export type EditSpaceViewModel = {
	model: SpaceViewModel;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
};

export function getEditSpaceViewModel(spaceId: number, signal?: AbortSignal): Promise<EditSpaceViewModel> {
	return httpClient.get<EditSpaceViewModel>(
		`/Workspace/Spaces/${spaceId}/Edit`,
		undefined,
		{ signal }
	);
}

export type SaveSpaceSettingsModel = {
	whoHasAccess: PermissionsUnionModel;
	owners: PermissionSetModel | null;
	name: string | null;
};

export type CreateSpaceResult = {
	id: number;
	userInfo: SpaceUserInfo;
};

export function createSpace(
	model: SaveSpaceSettingsModel,
	templateId?: number,
	signal?: AbortSignal
): Promise<CreateSpaceResult> {
	return httpClient.post<CreateSpaceResult>(
		isNotNil(templateId)
			? `/Workspace/Spaces?templateId=${templateId}`
			: "/Workspace/Spaces",
		model,
		{ signal }
	);
}

export type EditSpaceResult = {
	userInfo: SpaceUserInfo;
};

export function saveSpace(
	spaceId: number,
	model: SaveSpaceSettingsModel,
	signal?: AbortSignal
): Promise<EditSpaceResult> {
	return httpClient.put<EditSpaceResult>(
		`/Workspace/Spaces/${spaceId}`,
		model,
		{ signal }
	);
}

export function deleteSpace(
	spaceId: number,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.delete<void>(
		`/Workspace/Spaces/${spaceId}`,
		undefined,
		{ signal }
	);
}

export function getSharedTileGroupData(
	id: number,
	signal?: AbortSignal
): Promise<EditSharedGroupModel> {
	return httpClient.get<EditSharedGroupModel>(
		`/Workspace/EditSharedGroup/${id}`,
		undefined,
		{ signal }
	);
}

export function saveSharedTileGroupSettings(saveModel: SaveSharedTileGroupSettingsModel): Promise<void> {
	return httpClient.post<void>("/Workspace/SaveSharedTileGroupSettings", saveModel);
}

export function forceDefaultVisibility(tileGroupId: number): Promise<void> {
	return httpClient.post<void>(`/Workspace/ForceTileGroupDefaultVisibility/${tileGroupId}`, {});
}

export function registerTileUsage(tileId: number): Promise<void> {
	return httpClient.post<void>(
		`/Workspace/TileUsage/${tileId}`
	);
}

export type MaintenanceWindowTileModel = {
	endsOn: string;
	notificationMessage: string;
};

export type ConditionalAccessTileModel = {
	shouldHide: boolean;
	reason: string;
};

export type FrequentlyUsedTileModel = {
	tile: ClientTileModel;
	maintenanceWindow: MaintenanceWindowTileModel | null;
	conditionalAccess: ConditionalAccessTileModel | null;
};

export function listFrequentlyUsedTiles(
	signal?: AbortSignal
): Promise<readonly FrequentlyUsedTileModel[]> {
	return httpClient.get<readonly FrequentlyUsedTileModel[]>(
		"/Workspace/FrequentlyUsedTiles",
		undefined,
		{ signal }
	);
}

export type SpaceData = {
	id: number;
	name: string;
	kind: SpaceKind;
	tileGroupsPreferences: readonly TileGroupSpacePreferenceData[];
	accessKind: SpaceAccessKind;
};

export type TileGroupSpacePreferenceData = {
	tileGroupId: DatabaseOrTempObjectId;
	isHidden: boolean;
};

export interface SpaceInfo {
	id: number;
	name: string;
	kind: SpaceKind;
	accessKind: SpaceAccessKind;
}

export type AvailableAppAccessibilityModel = {
	isAccessible: boolean;
	tileWillBeHiddenWhenNotAccessible: boolean;
	reason: string | null;
};

export type AvailableAppModel = {
	installedAppId: number;
	name: string;
	color: AppColorViewModel;
	image: AppImageDisplayInfo;
	tileCanBeConfigured: boolean;
	accessibilityState: AvailableAppAccessibilityModel;
	appType: AppType;
	appCategory: AppCategory;
	tags: readonly TagViewModel[];
};

export type AvailableAppsViewModel = {
	availableApps: AvailableAppModel[];
};

export type SelectedTilesPostData = {
	addedApps: Pick<AvailableAppModel, "name" | "installedAppId">[];
	targetTileGroupId: number | null;
};

export type AddAppModel = {
	installedAppId: number;
	name: string;
};

export const enum AddTileTargetKind {
	NewTileGroup = 0,
	ExistingTileGroup = 1
}

export type AddTileTargetModel =
	| {
		kind: AddTileTargetKind.ExistingTileGroup;
		tileGroupId: number;
	}
	|
	{
		kind: AddTileTargetKind.NewTileGroup;
		tileGroupKind: TileGroupType;
		spaceId: number;
	};

export type AddTilesResponseModel = {
	recentlyAddedApps: readonly string[];
};

export function fetchTileModel(
	installedAppId: number,
	signal?: AbortSignal
): Promise<DetailsTileModel> {
	return httpClient.get<DetailsTileModel>(
		"Workspace/GetNewTileDetails",
		{ installedAppId },
		{
			signal,
		}
	);
};

export function getTileDetails(
	tileId: number,
	signal?: AbortSignal
): Promise<DetailsTileModel> {
	return httpClient.get<DetailsTileModel>(
		"Workspace/GetTileDetails",
		{ tileId },
		{ signal }
	);
}

export function fetchTiles(signal?: AbortSignal): Promise<AvailableAppsViewModel> {
	return httpClient.get<AvailableAppsViewModel>(
		"Workspace/GetAvailableApps",
		undefined,
		{ signal }
	);
};

export function addTilesToWorkspace(
	target: AddTileTargetModel,
	addedApps: readonly AddAppModel[]
): Promise<AddTilesResponseModel> {
	return httpClient.post<AddTilesResponseModel>(
		"Workspace/AddTilesToWorkspace",
		{ target, addedApps }
	);
}

export type TileGroupListItemModel = {
	id: number;
	name: string;
	type: TileGroupType;
};

export type AddTilesOnSpaceInformation = {
	spaceId: number;
	creatableTileGroupKinds: readonly TileGroupType[];
	tileGroups: readonly TileGroupListItemModel[];
};

export function getAddTilesOnSpaceInformation(
	spaceId: number,
	signal?: AbortSignal
): Promise<AddTilesOnSpaceInformation> {
	return httpClient.get<AddTilesOnSpaceInformation>(
		"Workspace/GetAddTilesOnSpaceInformation",
		{ spaceId },
		{ signal }
	);
};

export interface SharedTileGroupDefaultOrderModel {
	id: number;
	name: string;
	order: number;
}

export function getSharedTileGroupDefaultOrderList(signal?: AbortSignal): Promise<SharedTileGroupDefaultOrderModel[]> {
	return httpClient.get<SharedTileGroupDefaultOrderModel[]>(
		"Workspace/SharedTileGroupDefaultOrder",
		undefined,
		{ signal }
	);
}

export function saveSharedTileGroupDefaultOrderList(
	data: readonly number[],
	signal?: AbortSignal
): Promise<void> {
	return httpClient.put<void>(
		"Workspace/SharedTileGroupDefaultOrder",
		data,
		{ signal }
	);
}

export type DetailsTileModel =
	| AfasDetailsTileModel
	| CalendarAppDetailsTileModel
	| DocumentsAppDetailsTileModel
	| EmailAppDetailsTileModel
	| EmbedAppDetailsTileModel
	| GenericDetailsTileModel
	| IntegrationAppDetailsTileModel
	| PowerBiDetailsTileModel
	| RssDetailsTileModel
	| ShortcutDetailsTileModel
	| TeamsDetailsTileModel
	| ToDoAppDetailsTileModel
	| YammerDetailsTileModel
	| YouTubeDetailsTileModel
	| ZermeloDetailsTileModel
	| AddressBookDetailsTileModel;

export type GenericUpdateTileModel = {
	id: number;
	name: string;
	color: SaveAppColorModel;
	dimension: GridDimension;
	image: TileImage | null;
	shouldHideName: OptionalModel<boolean>;
};

export function updateGenericTile(
	appModel: GenericUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateGenericTile",
		appModel,
		{ signal }
	);
}

export interface GenericCreateTileModel {
	installedAppId: number;
	name: string;
	color: SaveAppColorModel;
	dimension: GridDimension;
	image: TileImage | null;
	shouldHideName: OptionalModel<boolean>;
}

export interface TileImage {
	id: number;
	type: AppImageType;
}

export interface GenericDetailsTileModel {
	name: string;
	defaultAppColor: AppColorViewModel;
	tileColor: AppColorViewModel;
	dimension: GridDimension;
	image: AppImageIdentifier;
	imageDisplayInfo: AppImageDisplayInfo;
	appType: AppType;
	canBeConfigured: boolean;
	shouldHideName: OptionalModel<boolean>;
}

export enum AddressBookViewKind {
	Workspace = 1,
	Department = 2,
	Personal = 3,
	Organization = 4
}

export type AddressBookUpdateTileModel = GenericUpdateTileModel & {
	defaultView: AddressBookViewKind | null;
};

export interface AddressBookDetailsTileModel extends GenericDetailsTileModel {
	defaultView: AddressBookViewKind;
}

export function updateAddressBookTile(
	appModel: AddressBookUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateAddressBookTile",
		appModel,
		{ signal }
	);
}

export interface AfasAppTabDetailsModel {
	uniqueId: string;
	name: string;
	resourceKind: AfasResourceKind;
}

export type UpdateAfasTabModel = {
	uniqueId: string;
	name: string;
	resourceKind: AfasResourceKind | undefined;
};

export type AfasUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
	tabs: UpdateAfasTabModel[];
};

export interface AfasDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
	tabs: AfasAppTabDetailsModel[];
}

export function updateAfasTile(
	appModel: AfasUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateAfasTile",
		appModel,
		{ signal }
	);
}

export type AzureDetailsTileModel = GenericDetailsTileModel & {
	destinationWindow: DestinationWindow;
	urlPrefix: string;
	urlSuffix: string;
};

export type AzureAppUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
};

export function updateAzureAppTile(
	appModel: AzureAppUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateAzureAppTile",
		appModel,
		{ signal }
	);
}

export interface AutotaskDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
}

export type AutotaskUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
};

export function updateAutotaskTile(
	appModel: AutotaskUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateAutotaskTile",
		appModel,
		{ signal }
	);
}

export interface ConnectWiseDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
}

export type ConnectWiseUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
};

export function updateConnectWiseTile(
	appModel: ConnectWiseUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateConnectWiseTile",
		appModel,
		{ signal }
	);
}

export interface EmbedAppDetailsTileModel extends GenericDetailsTileModel {
	hideHeader: boolean;
	hideFooter: boolean;
}

export type EmbedAppUpdateTileModel = GenericUpdateTileModel & {
	hideHeader: boolean;
	hideFooter: boolean;
};

export function updateEmbedAppTile(
	appModel: EmbedAppUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateEmbedAppTile",
		appModel,
		{ signal }
	);
}

export interface IntegrationAppDetailsTileModel extends GenericDetailsTileModel {
	connectionId: number;
	destinationWindow: DestinationWindow;
	navigationUrl: string | null;
	hideHeader: boolean;
	hideFooter: boolean;
	flowSettings: IntegrationTileFlowSettingsModel;
}

export type IntegrationUpdateTileModel = GenericUpdateTileModel & {
	hideHeader: boolean;
	hideFooter: boolean | null;
	layout: IntegrationTileLayoutModel | null;
};

export function updateIntegrationTile(
	appModel: IntegrationUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateIntegrationTile",
		appModel,
		{ signal }
	);
}

export interface GoogleAppDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
}

export type GoogleUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
};

export function updateGoogleTile(
	appModel: GoogleUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateGoogleTile",
		appModel,
		{ signal }
	);
}

export enum HubTileDefaultViewKind {
	AllItems = 1,
	Category = 2,
	CategoryCollection = 3
}

export type AllItemsHubTileDefaultViewConfigurationClientModel = {
	kind: HubTileDefaultViewKind.AllItems;
};

export type CategoryHubTileDefaultViewConfigurationClientModel = {
	kind: HubTileDefaultViewKind.Category;
	categoryId: number | null;
};

export type CategoryCollectionHubTileDefaultViewConfigurationClientModel = {
	kind: HubTileDefaultViewKind.CategoryCollection;
	categoryIds: readonly number[];
};

export type HubTileDefaultViewConfigurationClientModel =
	| AllItemsHubTileDefaultViewConfigurationClientModel
	| CategoryHubTileDefaultViewConfigurationClientModel
	| CategoryCollectionHubTileDefaultViewConfigurationClientModel;

export type DisplaySpotLightLayout = {
	kind: HubTileLayoutKind.Spotlight;
	itemKind: HubItemKind;
	itemId: number;
};

export type DisplayCarouselLayout = {
	kind: HubTileLayoutKind.Carousel;
	itemKind: HubItemKind;
	defaultView: HubTileDefaultViewConfigurationClientModel;
	isAutoPlayEnabled: boolean;
};

export type DisplayListLayout = {
	kind: HubTileLayoutKind.List;
	itemKind: HubItemKind;
	defaultView: HubTileDefaultViewConfigurationClientModel;
};

export type DisplayGridLayout = {
	kind: HubTileLayoutKind.Grid;
	itemKind: HubItemKind;
	defaultView: HubTileDefaultViewConfigurationClientModel;
};

export type DisplayFeedLayout = {
	kind: HubTileLayoutKind.Feed;
};

export type DisplayHubTileLayout =
	| DisplaySpotLightLayout
	| DisplayCarouselLayout
	| DisplayListLayout
	| DisplayGridLayout
	| DisplayFeedLayout;

export type SingleHubTileItemViewModel = {
	id: number;
	title: string;
};

export type DetailsSpotLightLayout = {
	kind: HubTileLayoutKind.Spotlight;
	itemKind: HubItemKind;
	item: SingleHubTileItemViewModel | null;
};

export type DetailsCarouselLayout = {
	kind: HubTileLayoutKind.Carousel;
	itemKind: HubItemKind;
	defaultView: HubTileDefaultViewConfigurationClientModel;
	isAutoPlayEnabled: boolean;
};

export type DetailsListLayout = {
	kind: HubTileLayoutKind.List;
	itemKind: HubItemKind;
	defaultView: HubTileDefaultViewConfigurationClientModel;
};

export type DetailsGridLayout = {
	kind: HubTileLayoutKind.Grid;
	itemKind: HubItemKind;
	defaultView: HubTileDefaultViewConfigurationClientModel;
};

export type DetailsFeedLayout = {
	kind: HubTileLayoutKind.Feed;
};

export type DetailsHubTileLayout =
	| DetailsSpotLightLayout
	| DetailsCarouselLayout
	| DetailsListLayout
	| DetailsGridLayout
	| DetailsFeedLayout;

export type HubAppDetailsTileModel = GenericDetailsTileModel & {
	layout: DetailsHubTileLayout;
	hideHeader: boolean;
	hideFooter: boolean;
	isCommunitiesFeatureAvailable: boolean;
};

export type HubTileDefaultViewConfigurationSaveModel =
	{
		kind: HubTileDefaultViewKind.AllItems;
	}
	|
	{
		kind: HubTileDefaultViewKind.Category;
		categoryId: number | null;
	}
	|
	{
		kind: HubTileDefaultViewKind.CategoryCollection;
		categoryIds: readonly number[];
	};

export type SaveHubTileLayoutModel =
	{
		kind: HubTileLayoutKind.Spotlight;
		itemKind: HubItemKind;
		itemId: number | null;
	}
	|
	{
		kind: HubTileLayoutKind.Carousel;
		itemKind: HubItemKind;
		defaultView: HubTileDefaultViewConfigurationSaveModel;
		isAutoPlayEnabled: boolean;
	}
	|
	{
		kind: HubTileLayoutKind.List;
		itemKind: HubItemKind;
		defaultView: HubTileDefaultViewConfigurationSaveModel;
	}
	|
	{
		kind: HubTileLayoutKind.Grid;
		itemKind: HubItemKind;
		defaultView: HubTileDefaultViewConfigurationSaveModel;
	}
	|
	{
		kind: HubTileLayoutKind.Feed;
	};

export type HubUpdateTileModel = GenericUpdateTileModel & {
	layout: SaveHubTileLayoutModel | null;
	hideHeader: boolean;
	hideFooter: boolean;
};

export function updateHubItemTile(
	appModel: HubUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateHubItemTile",
		appModel,
		{ signal }
	);
}

export interface LiquitDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
}

export type LiquitUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
};

export function updateLiquitTile(
	appModel: LiquitUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateLiquitTile",
		appModel,
		{ signal }
	);
}

export interface PowerBiDetailsTileModel extends GenericDetailsTileModel {
	powerBiGroupId: Guid | null;
	dashboardId: Guid;
	powerBiTileId: Guid;
	embedUrlBase: string;
	tokenInfo: TokenInfo;
}

export interface PowerBiUpdateTileModel extends GenericUpdateTileModel {
	powerBiGroupId: Guid | null;
	dashboardId: Guid | null;
	powerBiTileId: Guid | null;
}

export function updatePowerBiTile(
	appModel: PowerBiUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdatePowerBiTile",
		appModel,
		{ signal }
	);
}

export type PowerBiCreateTileModel = GenericCreateTileModel & {
	powerBiGroupId: Guid | null;
	dashboardId: Guid | null;
	powerBiTileId: Guid | null;
};

export function createPowerBiTile(
	model: CreateTileRequestModel<PowerBiCreateTileModel>,
	signal?: AbortSignal
): Promise<AddTilesResponseModel> {
	return httpClient.post<AddTilesResponseModel>(
		"Workspace/CreatePowerBiTile",
		model,
		{ signal }
	);
}

export interface SharePointDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
}

export interface SharePointUpdateTileModel extends GenericUpdateTileModel {
	destinationWindow: DestinationWindow;
}

export function updateSharePointTile(
	appModel: SharePointUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateSharePointTile",
		appModel,
		{ signal }
	);
}

export type ShortcutDetailsTileModel = GenericDetailsTileModel & {
	destinationWindow: DestinationWindow;
	uri: string;
};

export type ShortcutUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
	uri: string;
};

export function updateShortcutTile(
	appModel: ShortcutUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateShortcutTile",
		appModel,
		{ signal }
	);
}

export type ShortcutCreateTileModel = GenericCreateTileModel & {
	destinationWindow: DestinationWindow;
	uri: string;
};

export function createShortcutTile(
	model: CreateTileRequestModel<ShortcutCreateTileModel>,
	signal?: AbortSignal
): Promise<AddTilesResponseModel> {
	return httpClient.post<AddTilesResponseModel>(
		"Workspace/CreateShortcutTile",
		model,
		{ signal }
	);
}

export interface TeamsDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
}

export type TeamsUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
};

export function updateTeamsTile(
	appModel: TeamsUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateTeamsTile",
		appModel,
		{ signal }
	);
}

export interface TopDeskDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
}

export type TopDeskUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
};

export function updateTopDeskTile(
	appModel: TopDeskUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateTopDeskTile",
		appModel,
		{ signal }
	);
}

export interface VilansKickAppDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
}

export type VilansKickUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
};

export function updateVilansKickTile(
	appModel: VilansKickUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateVilansKickTile",
		appModel,
		{ signal }
	);
}

export type YouTubeDetailsTileModel = GenericDetailsTileModel & {
	youTubeUrl: string;
	hideHeader: boolean;
	hideFooter: boolean;
};

export type YouTubeUpdateTileModel = GenericUpdateTileModel & {
	youTubeUrl: string;
	hideHeader: boolean;
	hideFooter: boolean;
};

export function updateYouTubeTile(
	appModel: YouTubeUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateYouTubeTile",
		appModel,
		{ signal }
	);
}

export type YouTubeCreateTileModel = GenericCreateTileModel & {
	youTubeUrl: string;
	hideHeader: boolean;
	hideFooter: boolean;
};

export function createYouTubeTile(
	model: CreateTileRequestModel<YouTubeCreateTileModel>,
	signal?: AbortSignal
): Promise<AddTilesResponseModel> {
	return httpClient.post<AddTilesResponseModel>(
		"Workspace/CreateYouTubeTile",
		model,
		{ signal }
	);
}

export interface ZermeloDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
}

export interface ZermeloUpdateTileModel extends GenericUpdateTileModel {
	destinationWindow: DestinationWindow;
}

export function updateZermeloTile(
	appModel: ZermeloUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateZermeloTile",
		appModel,
		{ signal }
	);
}

export type CalendarAppDetailsTileModel = GenericDetailsTileModel & {
	destinationWindow: DestinationWindow;
};

export type CalendarUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
};

export function updateCalendarTile(
	appModel: CalendarUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateCalendarTile",
		appModel,
		{ signal }
	);
}

export interface DocumentsAppTabDetailsModel {
	uniqueId: string;
	name: string;
	folder: DocumentsFolderIdentifier;
}

type UpdateRecentFolderModel = {};
type UpdateBookmarksFolderModel = {};
type UpdateSharePointFolderModel = {
	siteUrl: string;
	listId: Guid;
	id: number | null;
	name: string;
};

type UpdateWebDavFolderModel = {
	serverId: number;
	id: string;
	name: string;
};

export type RecentUpdateDocumentsFolderModel = { recent: UpdateRecentFolderModel; };

export type BookmarksUpdateDocumentsFolderModel = { bookmarks: UpdateBookmarksFolderModel; };

export type SharePointUpdateDocumentsFolderModel = { sharePoint: UpdateSharePointFolderModel; };

export type WebDavUpdateDocumentsFolderModel = { webDav: UpdateWebDavFolderModel; };

export type UpdateDocumentsFolderModel = SharePointUpdateDocumentsFolderModel
	| WebDavUpdateDocumentsFolderModel
	| RecentUpdateDocumentsFolderModel
	| BookmarksUpdateDocumentsFolderModel;

export type UpdateDocumentsAppTabModel = {
	uniqueId: string;
	name: string;
	folder: UpdateDocumentsFolderModel | null;
};

export type DocumentsUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
	tabs: UpdateDocumentsAppTabModel[];
};

export interface DocumentsAppDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
	tabs: DocumentsAppTabDetailsModel[];
}

export function updateDocumentsTile(
	appModel: DocumentsUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateDocumentsTile",
		appModel,
		{ signal }
	);
}

export interface EmailAppDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
	tabs: EmailAppTabDetailsModel[];
	isAdmin: boolean;
	defaultMailboxSmtpAddress: string | null;
	sharedMailboxes: string[];
}

export type EmailAppTabDetailsModel = {
	uniqueId: string;
	name: string;
	contentKind: ContentKind;

	user?: UserMailboxDetailsModel;
	personal?: PersonalMailboxDetailsModel;
	shared?: SharedMailboxDetailsModel;
	other?: OtherMailboxDetailsModel;
};

type UserMailboxDetailsModel = {
	folder: WellKnownFolderIdentifier;
};

type PersonalMailboxDetailsModel = {
	folder: EmailFolderIdentifier;
};

type SharedMailboxDetailsModel = {
	mailbox: SpecificMailboxIdentifier;
	folder: EmailFolderIdentifier;
};

type OtherMailboxDetailsModel = {
	mailbox: SpecificMailboxIdentifier;
	folder: EmailFolderIdentifier;
};

export type UpdateOtherMailboxModel = {
	mailbox: SpecificMailboxIdentifierModel;
	folder: FolderIdentifierModel | null;
};

export type UpdateEmailAppTabModel = {
	uniqueId: string;
	name: string;
	contentKind: ContentKind;

	user?: UpdateUserMailboxModel;
	personal?: UpdatePersonalMailboxModel;
	shared?: UpdateSharedMailboxModel;
	other?: UpdateOtherMailboxModel;
};

export type UpdateUserMailboxModel = {
	folder: WellKnownFolderIdentifierModel | null;
};

export type UpdatePersonalMailboxModel = {
	folder: FolderIdentifierModel | null;
};

export type UpdateSharedMailboxModel = {
	mailbox: SpecificMailboxIdentifierModel;
	folder: FolderIdentifierModel | null;
};

export type EmailUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
	tabs: UpdateEmailAppTabModel[];
};

export function updateEmailTile(
	appModel: EmailUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateEmailTile",
		appModel,
		{ signal }
	);
}

interface MagisterAppTabDetailsModel {
	uniqueId: string;
	name: string;
	resourceKind: MagisterResourceKind;
}

export interface MagisterAppDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
	tabs: readonly MagisterAppTabDetailsModel[];
}

export type UpdateMagisterTabModel = {
	uniqueId: string;
	name: string;
	resourceKind: MagisterResourceKind | undefined;
};

export type MagisterUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
	tabs: UpdateMagisterTabModel[];
};

export function updateMagisterTile(
	appModel: MagisterUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateMagisterTile",
		appModel,
		{ signal }
	);
}

interface NedapOnsAppTabDetailsModel {
	uniqueId: string;
	name: string;
	resourceKind: NedapOnsResourceKind;
}

export type UpdateNedapOnsTabModel = {
	uniqueId: string;
	name: string;
	resourceKind: NedapOnsResourceKind | undefined;
};

export type NedapOnsUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
	tabs: UpdateNedapOnsTabModel[];
};

export interface NedapOnsDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
	tabs: NedapOnsAppTabDetailsModel[];
}

export function updateNedapOnsTile(
	appModel: NedapOnsUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateNedapOnsTile",
		appModel,
		{ signal }
	);
}

export enum RssFeedConfigurationSource {
	Own = 0,
	Predefined = 1
}

interface RssTabWithOwnConfiguration {
	uniqueId: string;
	configurationSource: RssFeedConfigurationSource.Own;
	name: string;
	feedUrl: string;
	feedType: RssFeedType;
	siteUrl: string;
}

interface RssTabWithPredefinedConfiguration {
	uniqueId: string;
	configurationSource: RssFeedConfigurationSource.Predefined;
	feedId: number;
	name: string;
	feedUrl: string;
	siteUrl: string;
}

export type RssTab =
	| RssTabWithOwnConfiguration
	| RssTabWithPredefinedConfiguration;

export interface RssFeedModel {
	id: number;
	name: string;
	feedUrl: string;
	feedType: RssFeedType;
	siteUrl: string;
}

export interface RssDetailsTileModel extends GenericDetailsTileModel {
	urlSuffix: string;
	tabs: RssTab[];
	predefinedFeeds: RssFeedModel[];
}

export type PredefinedRssAppTab = {
	configurationSource: RssFeedConfigurationSource.Predefined;
	uniqueId: Guid;
	feedId: number;
};

export type OwnRssAppTab = {
	configurationSource: RssFeedConfigurationSource.Own;
	uniqueId: Guid;
	name: string;
	feedUrl: string;
	feedType: RssFeedType;
	siteUrl: string;
};

export type RssAppTab = PredefinedRssAppTab |
	OwnRssAppTab;

export type RssUpdateTileModel = GenericUpdateTileModel & {
	tabs: RssAppTab[];
};

export function updateRssTile(
	appModel: RssUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateRssTile",
		appModel,
		{ signal }
	);
}

export type RssCreateTileModel = GenericCreateTileModel & {
	tabs: RssAppTab[];
};

export function createRssTile(
	model: CreateTileRequestModel<RssCreateTileModel>,
	signal?: AbortSignal
): Promise<AddTilesResponseModel> {
	return httpClient.post<AddTilesResponseModel>(
		"Workspace/CreateRssTile",
		model,
		{ signal }
	);
}

export type ToDoUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
};

export interface ToDoAppDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
}

export function updateToDoTile(
	appModel: ToDoUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateToDoTile",
		appModel,
		{ signal }
	);
}

type UpdateYammerTabModelBase = {
	uniqueId: Guid;
	name: string;
};

export type UpdateYammerTabModelWithFeedId = UpdateYammerTabModelBase & {
	kind: YammerFeedKind.OtherGroup | null;
	feedId: number | null;
};

type UpdateYammerTabModelWithoutFeedId = UpdateYammerTabModelBase & {
	kind: YammerFeedKind.AllCompany | YammerFeedKind.Home | null;
};

export type UpdateYammerTabModel = UpdateYammerTabModelWithFeedId | UpdateYammerTabModelWithoutFeedId;

type YammerAppTabDetailsModel = {
	uniqueId: Guid;
	name: string;
	feedId: number | null;
	kind: YammerFeedKind;
};

export type YammerUpdateTileModel = GenericUpdateTileModel & {
	tabs: UpdateYammerTabModel[];
};

export type YammerDetailsTileModel = GenericDetailsTileModel & {
	tabs: YammerAppTabDetailsModel[];
};

export function updateYammerTile(
	appModel: YammerUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateYammerTile",
		appModel,
		{ signal }
	);
}

interface ZaaksysteemAppTabDetailsModel {
	uniqueId: string;
	name: string;
	resourceKind: ZaaksysteemResourceKind;
}

export type UpdateZaaksysteemTabModel = {
	uniqueId: string;
	name: string;
	resourceKind: ZaaksysteemResourceKind | null;
};

export type ZaaksysteemUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
	tabs: UpdateZaaksysteemTabModel[];
};

export interface ZaaksysteemDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
	tabs: ZaaksysteemAppTabDetailsModel[];
}

export function updateZaaksysteemTile(
	appModel: ZaaksysteemUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateZaaksysteemTile",
		appModel,
		{ signal }
	);
}

interface ZenyaDocAppTabDetailsModel {
	uniqueId: string;
	name: string;
	resourceKind: ZenyaDocResourceKind;
}

export type UpdateZenyaDocTabModel = {
	uniqueId: string;
	name: string;
	resourceKind: ZenyaDocResourceKind | null;
};

export type ZenyaDocUpdateTileModel = GenericUpdateTileModel & {
	destinationWindow: DestinationWindow;
	tabs: UpdateZenyaDocTabModel[];
};

export interface ZenyaDocDetailsTileModel extends GenericDetailsTileModel {
	destinationWindow: DestinationWindow;
	tabs: ZenyaDocAppTabDetailsModel[];
}

export function updateZenyaDocTile(
	appModel: ZenyaDocUpdateTileModel,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.post<void>(
		"Workspace/UpdateZenyaDocTile",
		appModel,
		{ signal }
	);
}

export type CreateTileRequestModel<TTileModel extends GenericCreateTileModel> = {
	target: AddTileTargetModel;
	appModel: TTileModel;
};

export enum HubItemTileResourceKind {
	CategoryIds = 1,
	ItemIds = 2
}

export type HubItemTileResourceId =
	| {
		kind: HubItemTileResourceKind.CategoryIds;
		categoryIds: readonly number[] | null;
	}
	| {
		kind: HubItemTileResourceKind.ItemIds;
		itemIds: readonly number[];
	};